/**
 * WAITING — practice mode while waiting for opponents to fill the room.
 *
 * UI surface:
 *   - profile cards visible, "shooting" mode (no betting framing)
 *   - instruction banner: game name + "waiting for players"; no countdown, no lock-in
 *   - camera at default anchor
 *   - practice dice on the table (3 for cee-lo, 2 for craps) so the
 *     player can fidget while waiting; cleared on STARTING entry
 *
 * Updates only need to refresh the player roster (joins / leaves) and
 * keep the dice-result cache empty (a leftover from a previous round
 * shouldn't linger into a new game). No betting / roll / animation
 * concerns live in this state.
 */

import type { GameUpdate } from '@sdl/shared';
import { State } from '../State.js';

export class WaitingState extends State {
  async onEnter(update: GameUpdate): Promise<void> {
    const game = this.game;

    // Restore the shooting baseline (lights + betting teardown) BEFORE the rest
    // of the waiting setup. Covers the mid-round-leave abandon, where the server
    // jumps the surviving player straight from a betting phase to WAITING: only
    // roll states call exitBettingMode, so without this the lights stayed on the
    // betting anchor and the still-running betting controller ate the player's
    // input. Idempotent — a no-op on the normal between-games WAITING entry.
    game.ensureShootingView();

    // Visible UI baseline for waiting: profiles in shooting mode, countdown
    // and lock-in cleared.
    game.playerProfilesOverlay?.show();
    game.playerProfilesOverlay?.setMode('shooting');
    game.countdownOverlay?.stop();
    game.bettingHUD3D?.setLockInVisible(false);

    // Reset opponent dice rigs + result labels. On the normal between-games
    // path GameEndState already did this, but a mid-round void jumps straight
    // here from a roll phase — without this the other players' dice from the
    // dead round stay lying on the table through the whole waiting period.
    // Idempotent on the normal path.
    game.opponentDiceManager?.resetAllOpponents();
    game.opponentDiceManager?.hideAllResultLabels();

    // Title (game name) + subtitle banner — same surface HI_LO uses — so the
    // waiting player knows the mode and that we're holding for opponents while
    // they fidget with the practice dice.
    game.instructionBanner?.show({
      title: update.gameType === 'craps' ? 'Street Craps' : 'Cee-Lo',
      text: 'Waiting for players to join…',
    }, update.animated);

    // Camera back to default — covers the case where the previous round
    // ended with the camera at a betting / zoom anchor.
    game.cameraController?.setMode('default');

    // Practice shooting is unauthorized: re-arm the dice controller for FREE
    // pickup. A prior game switches it into server-authoritative mode (set on
    // every roll-authorize, never reset) and may have left input disabled — so
    // after a mid-game abandon the lone waiting player fails the pickup gate
    // (`!authoritative || authorized`) and the dice are untappable. Fresh-room
    // WAITING worked only because the default is non-authoritative. Idempotent.
    game.diceController?.setServerAuthoritativeMode(false);
    game.diceController?.setEnabled(true);
    // Reset the throw state machine to READY: a void can land here right
    // after the local player's own throw (controller settled at STOPPED),
    // and GameFlowController.update suppresses the instruction banner every
    // frame while the state isn't READY — leaving the "waiting for players"
    // banner invisible for the whole waiting period.
    game.diceController?.resetToReady();

    // Practice dice. Skip recreate when the table already shows dice AT the
    // practice slots so late joiners don't see a flash of dice replacement.
    // Count alone is NOT enough: a mid-round void lands here with the dead
    // round's dice where they fell, and a count-only check adopted those as
    // practice dice — the previous roll stayed lying mid-table with PICK UP
    // DICE labels floating over it.
    const practiceCount = update.gameType === 'craps' ? 2 : 3;
    if (game.diceController) {
      const slots = this.practicePositions(practiceCount);
      const dice = game.diceController.getDice();
      const atPracticeSlots = dice.length === practiceCount
        && dice.every((die, i) => {
          const dx = die.mesh.position.x - slots[i].x;
          const dz = die.mesh.position.z - slots[i].z;
          return Math.hypot(dx, dz) < 1.0;
        });
      if (!atPracticeSlots) {
        game.diceController.createDice(slots);
      }
      game.cameraController?.setDice(game.diceController.getDice());
    }

    // Fresh round → clear dice-result dedup so any leftover icons
    // recompute (typically clears them, since `player.result` is null
    // during WAITING).
    game.clearResultDedup();
    this._updateProfiles(update);
    await this._checkPlayers(update);
  }

  async onUpdate(update: GameUpdate): Promise<void> {
    // The only thing that changes during WAITING is the player roster
    // (join / leave / balance). Refresh profiles + reconcile any dice
    // result the cache hasn't seen yet (e.g. very-late joiner where
    // server still has player.result populated from before reset).
    this._updateProfiles(update);
    await this._checkPlayers(update);
  }

  async onExit(_update: GameUpdate): Promise<void> {
    // Clear the practice dice now that warm-up is over. The next state
    // (STARTING / HI_LO / SHOOTER_SET_STACK) doesn't need them on the
    // table; HI_LO and roll states spawn their own dice via the
    // dice-authorize flow.
    const game = this.game;
    game.diceController?.clearDice();
    game.cameraController?.setDice([]);
  }

  private practicePositions(count: number): Array<{ x: number; y: number; z: number }> {
    if (count === 2) {
      return [
        { x: -0.75, y: 1, z: 5 },
        { x: 0.75, y: 1, z: 5 },
      ];
    }
    return [
      { x: -1.5, y: 1, z: 5 },
      { x: 0, y: 1, z: 5 },
      { x: 1.5, y: 1, z: 5 },
    ];
  }
}
